import type { ClientFindingField } from "../../enums/client-finding";
import { CLIENT_FINDING_FIELD } from "../../enums/client-finding";

const FIELD_LIMITS: Record<ClientFindingField, number> = {
  title: 180,
  summary: 520,
  action: 320,
};

const PROMPT_CONTROL_PREFIX =
  /^(?:#{1,6}\s*|[-*]\s*)?(?:\*\*|__)?(?:summary|finding|recommended\s*action|recommendedaction|action|why\s+it\s+matters)(?:\s*:\s*(?:\*\*|__)?|(?:\*\*|__)\s*:\s*)/i;

const NAMED_EXTERNAL_AI_PRODUCT =
  /\b(?:chatgpt|perplexity|claude(?:\s+ai)?|gemini|google\s+ai(?:\s+overviews?)?)\b/i;
const EXTERNAL_AI_OUTCOME =
  /\b(?:citations?|cited|citing|misrepresent(?:ed|ation)?|external\s+representation|competitor\s+leakage)\b/i;
const COMMERCIAL_OUTCOME =
  /\b(?:referral\s+traffic|ai\s+traffic|commercial\s+(?:impact|performance|outcomes?)|financial\s+return|recoverable\s+(?:score|points?)|return\s+on\s+investment|roi|revenue|sales)\b/i;
const CLAIMED_MEASURED_BEHAVIOUR =
  /\b(?:measured|observed|confirmed)\s+(?:customer|user|visitor)\s+behaviou?r\b/i;
const CLAIMED_CONVERSION_OR_TRAFFIC_EFFECT =
  /\b(?:increase|decrease|improve|reduce|lose|gain|drive|harm|damage|cost)\w*\b[^.]{0,80}\b(?:traffic|conversions?|conversion\s+rate|revenue|sales)\b/i;
const ACTION_CLAUSE =
  /\s+(?:so(?:\s+that)?|to|which\s+(?:will|would|can|could)|thereby|that\s+(?:lets?|allows?|enables?))\s+/gi;
const NAMED_CRAWLER =
  /\b(?:gptbot|chatgpt-user|oai-searchbot|claude(?:bot|-searchbot|-user|-web)?|perplexity(?:bot|-user)?|google-extended|google-cloudvertexbot|meta-external(?:agent|fetcher)|applebot-extended|duckassistbot|aasa)\b/gi;
const NAMED_CRAWLER_LIST =
  /\s*\((?:[^)]*\b(?:gptbot|chatgpt-user|oai-searchbot|claude(?:bot|-searchbot|-user|-web)?|perplexity(?:bot|-user)?|google-extended|google-cloudvertexbot|meta-external(?:agent|fetcher)|applebot-extended|duckassistbot|aasa)\b[^)]*)\)/gi;

function hasUnsupportedOutcome(value: string): boolean {
  return (
    EXTERNAL_AI_OUTCOME.test(value) ||
    COMMERCIAL_OUTCOME.test(value) ||
    CLAIMED_MEASURED_BEHAVIOUR.test(value) ||
    CLAIMED_CONVERSION_OR_TRAFFIC_EFFECT.test(value)
  );
}

function stripUnsupportedActionTail(value: string): string {
  let start: number | null = null;
  for (const match of value.matchAll(ACTION_CLAUSE)) {
    if (hasUnsupportedOutcome(value.slice(match.index))) {
      start = match.index ?? null;
    }
  }
  return start === null ? value : value.slice(0, start);
}

export function stripFindingControlPrefixes(value: string): string {
  return value
    .trim()
    .split("\n")
    .map((line) => line.trim().replace(PROMPT_CONTROL_PREFIX, ""))
    .filter(Boolean)
    .join("\n");
}

export function clientFindingTextViolation(
  value: string,
  field: ClientFindingField,
): string | null {
  const trimmed = value.trim();
  if (!trimmed) return `${field} must not be empty`;
  if (trimmed.split("\n").some((line) => PROMPT_CONTROL_PREFIX.test(line))) {
    return `${field} must not include prompt-control labels such as Summary: or RecommendedAction:`;
  }
  if (trimmed.length > FIELD_LIMITS[field]) {
    return `${field} must be ${FIELD_LIMITS[field]} characters or fewer`;
  }
  if (NAMED_EXTERNAL_AI_PRODUCT.test(trimmed)) {
    return `${field} must not name an external answer-engine product without external-outcome evidence`;
  }
  if (
    EXTERNAL_AI_OUTCOME.test(trimmed) ||
    COMMERCIAL_OUTCOME.test(trimmed) ||
    CLAIMED_MEASURED_BEHAVIOUR.test(trimmed) ||
    CLAIMED_CONVERSION_OR_TRAFFIC_EFFECT.test(trimmed)
  ) {
    return `${field} must not claim external AI, traffic, commercial, conversion, or measured behavioural outcomes from owned-site evidence`;
  }
  const sentenceCount = trimmed.match(/[.!?](?:\s|$)/g)?.length ?? 0;
  const sentenceLimit =
    field === CLIENT_FINDING_FIELD.SUMMARY
      ? 3
      : field === CLIENT_FINDING_FIELD.ACTION
        ? 2
        : 1;
  if (sentenceCount > sentenceLimit) {
    return `${field} must be concise (${sentenceLimit} sentence${sentenceLimit === 1 ? "" : "s"} maximum)`;
  }
  return null;
}

/**
 * Legacy records predate the client-copy guard. A safe primary rendering may
 * remove prompt labels, but it never rewrites unsupported claims into facts.
 */
export function clientFacingFindingText(
  value: string | null | undefined,
  field: ClientFindingField,
): string | null {
  if (!value) return null;
  const stripped = stripFindingControlPrefixes(value);
  return clientFindingTextViolation(stripped, field) ? null : stripped;
}

/**
 * Legacy recommendations can combine a concrete site change with an
 * unsupported outcome claim. Preserve the concrete imperative for client
 * surfaces, while keeping the original text available as technical material.
 */
export function clientFacingActionText(
  value: string | null | undefined,
): string | null {
  const direct = clientFacingFindingText(value, CLIENT_FINDING_FIELD.ACTION);
  if (direct) return direct;
  if (!value) return null;

  const firstSentence =
    stripFindingControlPrefixes(value).match(/^.*?[.!?](?:\s|$)/)?.[0] ??
    stripFindingControlPrefixes(value);
  let action = firstSentence
    .replace(NAMED_CRAWLER_LIST, "")
    .replace(NAMED_CRAWLER, "")
    .trim();

  action = stripUnsupportedActionTail(action)
    .replace(/\s+,\s*,/g, ",")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,])/g, "$1")
    .trim();

  if (/^(?:allow|permit|unblock)\b\.?$/i.test(action)) {
    action =
      "Review the crawl policy and update robots.txt to allow the search and AI crawlers the organisation approves.";
  }

  return clientFindingTextViolation(action, CLIENT_FINDING_FIELD.ACTION)
    ? null
    : action;
}
